import { motion } from "motion/react";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import TopProgressBar from "~/components/new/TopProgressBar";
import { SelectedStore, useBookingFlowStore } from "~/stores/booking-flow-store";
import { useLanguageStore } from "~/stores/language-store";
import useGTM from "~/components/gtmProvider/useGTM";
import { GTMEvent } from "~/components/gtmProvider/GTMContext";
import { isEmbedded } from "~/lib/embed";
import StoreSelection, { mapStoreToSelectedStore } from "./StoreSelection";

// ──────────────────────────── Not Found ────────────────────────────

const NotFoundState = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { t } = useLanguageStore();

    const restartBooking = () => {
        if (isEmbedded()) {
            navigate("/");
            return;
        }
        window.open("https://kalialab.de/termin-buchen", "_self");
    };

    return (
        <div className='flex flex-col items-center justify-center gap-6 flex-1 text-center px-4'>
            <div className='w-16 h-16 rounded-full bg-burgundy/10 flex items-center justify-center'><span className='text-2xl text-burgundy/50'>?</span></div>
            <div className='flex flex-col gap-2'>
                <span className='text-xl text-burgundy font-normal'>{t('deeplinkNotFound')}: &quot;{slug}&quot;</span>
                <span className='text-sm text-burgundy/70 font-light'>{t('deeplinkNotFoundDescription')}</span>
            </div>
            <PrimaryActionButton onClick={restartBooking} label={t('backToBeginning') || 'Neue Buchung starten'} />
        </div>
    );
};

// ──────────────────────────── Main DeepLink component ────────────────────────────

const DeepLink = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { sendEvent } = useGTM();

    const {
        setSelectedStore, setSelectedPath, setCartItems, setDeeplinkSlug,
        setSelectedTimeslot, setDiscountCode, setMatchedDiscountCode,
        setDeeplinkPractitionerId, setDeeplinkItem, setDeeplinkCategoryId, setDeeplinkTargetCategoryId,
    } = useBookingFlowStore(useShallow((state) => ({
        setSelectedStore: state.setSelectedStore,
        setSelectedPath: state.setSelectedPath,
        setCartItems: state.setCartItems,
        setDeeplinkSlug: state.setDeeplinkSlug,
        setSelectedTimeslot: state.setSelectedTimeslot,
        setDiscountCode: state.setDiscountCode,
        setMatchedDiscountCode: state.setMatchedDiscountCode,
        setDeeplinkPractitionerId: state.setDeeplinkPractitionerId,
        setDeeplinkItem: state.setDeeplinkItem,
        setDeeplinkCategoryId: state.setDeeplinkCategoryId,
        setDeeplinkTargetCategoryId: state.setDeeplinkTargetCategoryId,
    })));

    const hasEnteredFunnel = useRef(false);
    const hasFiredInit = useRef(false);

    // ── API ──
    const { data: resolvedData, isLoading } = trpc.publicBookingFunnel.resolveDeeplink.useQuery(
        { slug: slug || '' },
        { enabled: !!slug, refetchOnMount: true, refetchOnWindowFocus: false }
    );

    const config = resolvedData?.config;

    const storeData: SelectedStore[] = useMemo(() => {
        if (!resolvedData?.stores) return [];
        return resolvedData.stores.map(mapStoreToSelectedStore);
    }, [resolvedData?.stores]);

    useEffect(() => {
        setSelectedStore(null);
        hasEnteredFunnel.current = false;
        hasFiredInit.current = false;
        // Set the slug early so every analytics event from here on is tagged as a deeplink session.
        setDeeplinkSlug(slug || null);
        setDeeplinkPractitionerId(null);
        setDeeplinkCategoryId(null);
        setDeeplinkTargetCategoryId(null);
    }, [slug]);

    // Fire the funnel-start event once the deeplink resolves (mirrors StoreSelection's INIT)
    useEffect(() => {
        if (isLoading || !resolvedData || hasFiredInit.current) return;
        hasFiredInit.current = true;
        setDeeplinkTargetCategoryId(resolvedData.targetType === 'CATEGORY' ? resolvedData.categoryId : null);
        sendEvent(GTMEvent.BOOKING_FUNNEL_INIT, {
            user: { id: null, has_account: false, email: null, phone: null },
        });
    }, [isLoading, resolvedData]);

    // ── Handoff into the real booking funnel ──
    const enterFunnel = (store: SelectedStore) => {
        if (!resolvedData || hasEnteredFunnel.current) return;
        const isCategoryDeeplink = resolvedData.targetType === 'CATEGORY';
        const item = isCategoryDeeplink ? null : resolvedData.bookableItemsByStoreId[String(store.id)];
        if (!isCategoryDeeplink && !item) return;
        hasEnteredFunnel.current = true;
        setSelectedStore(store);
        setSelectedPath('browse-by-category');
        setCartItems([]);
        setSelectedTimeslot(null);
        setDiscountCode('');
        setMatchedDiscountCode(null);
        setDeeplinkSlug(slug || null);
        setDeeplinkPractitionerId(resolvedData.practitionerId ?? null);
        setDeeplinkItem(item ?? null);
        setDeeplinkCategoryId(isCategoryDeeplink ? resolvedData.categoryId : null);
        setDeeplinkTargetCategoryId(isCategoryDeeplink ? resolvedData.categoryId : null);

        // Fire the store + path selection events that the deeplink flow skips, so the
        // deeplink funnel tracks the same steps as the normal funnel (tagged as deeplink).
        sendEvent(GTMEvent.BOOKING_FUNNEL_BRANCH_SELECTED, {
            branch: { id: store.id, name: store.name },
        });
        sendEvent(GTMEvent.BOOKING_FUNNEL_PATH_SELECTION, {});
        sendEvent(GTMEvent.BOOKING_FUNNEL_PATH_SELECTED, { selected_path: 'browse-by-category' });

        navigate("/services");
    };

    const notFound = !isLoading && !resolvedData;

    // ──────────────── RENDER ────────────────

    if (notFound) {
        return (
            <motion.div className='flex flex-col min-h-screen' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: "easeOut" }}>
                <TopProgressBar percentageFilled={20} displayString="DEEPLINK" />
                <div className='flex-1 flex flex-col px-4 pt-8 pb-8'><NotFoundState /></div>
            </motion.div>
        );
    }

    // The store step is the standard funnel's store selection, fed with the deeplink's
    // eligible stores and handing off straight into the treatment or category it points at.
    return (
        <StoreSelection
            deeplink={{
                isLoading,
                config: config ?? null,
                stores: storeData,
                percentageFilled: 33,
                progressFallbackDe: 'SCHRITT 1 / 3',
                progressFallbackEn: 'STEP 1 / 3',
                onContinue: enterFunnel,
            }}
        />
    );
};

export default DeepLink;
